import { useEffect, useRef, useState } from "react";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import DashboardCard from "./common/DashboardCard";
import Tracks from "./Tracks";
import moment from "moment";
import { downloadUtilizationReport, getResourcesData } from "src/services/ResourcePoolService";
import { useDispatch, useSelector } from "react-redux";
import { AppActions } from "src/store/AppReducer";
import GetAppIcon from '@mui/icons-material/GetApp';
import fileDownload from "js-file-download";
import SnackBar from "src/components/SnackBar/SnackBar";
import { RootStateType } from "src/store/RootReducer";
import DateRangePicker from "react-bootstrap-daterangepicker";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MultiSelect from "../common/MultiSelect"; 

interface DashboardProps {
  permissions: any
}

const ResourcePoolDashboard = (props: DashboardProps) => {
  const myRef = useRef<DateRangePicker>();
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer.user);

  const [tracks, setTracks] = useState<any>([]);
  const [trackDetails, setTrackDetails] = useState<any>([]);
  const [utilized, setUtilized] = useState<any>();

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [dateChanged, setDateChanged] = useState(true);
  const [type, setType] = useState(['SHARED_SERVICE', 'DELIVERY']);
  const [allocationType, setAllocationType] = useState(['Billable', 'Non-Billable', 'Shadow', 'Support', 'T&M']);
  const [startDate, setStartDate] = useState<any>(moment(new Date()).format('YYYY/MM/DD'));
  const [endDate, setEndDate] = useState<any>(moment(new Date()).format('YYYY/MM/DD'));

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      myRef.current.setStartDate(new Date(startDate));
      myRef.current.setEndDate(new Date(endDate));
    }
  }, []);

  useEffect(() => {
    if(dateChanged || type.length > 0 || allocationType.length > 0){
      const isShared = type.includes('SHARED_SERVICE');
      const isDelivery = type.includes('DELIVERY');
      getTracks(startDate, endDate, isShared, isDelivery, allocationType);
    }
  }, [dateChanged, type, allocationType]);

  const getTracks = (startDate, endDate, isShared, isDelivery, allocationType) => {
    dispatch(AppActions.showLoader());
    getResourcesData(moment(startDate).format('MM/DD/YYYY'), moment(endDate).format('MM/DD/YYYY'), isShared, isDelivery, allocationType).then((res) => {
      const data = res?.data;      
      setTrackDetails(getTracksDetails(data));
      setTracks(data);
      setDateChanged(false);
      dispatch(AppActions.hideLoader());
    });
  }

  const setOrder = (data) => {
    const trackOrder = ['Dev', 'QA', 'BA', 'PM', 'SS']
    const newData = [];

    for(let i=0; i < trackOrder.length; i++){
      const el = data?.filter(e => e?.trackCode === trackOrder[i])[0];
      if(el){
        newData.push(el);
      }
    }
    return newData;
  }

  const handleDownload = () => {
    dispatch(AppActions.showLoader());
    downloadUtilizationReport(moment(startDate).format('MM/DD/YYYY'), moment(endDate).format('MM/DD/YYYY'), allocationType)
    .then((res) => {
      if(res.status === 200) {
        fileDownload(res.data, 'utilization-report.xlsx');
      }else{
        setSnackBarMessage('No data in selected date range.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      }
      dispatch(AppActions.hideLoader());
    })
    .catch((err) => {
      setSnackBarMessage('No data were taken in selected date range.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      dispatch(AppActions.hideLoader());
    });
  }

  const getInitSettings = () => {
    const today = new Date();
    let settings = {
      startDate: moment(today).format('YYYY/MM/DD'),
      endDate: moment(today).format('YYYY/MM/DD'),
    };
    return settings;
  };


  const getTracksDetails = (tracks) => {
    const data = tracks?.data;
    const activeResourcesList = data?.flatMap(track => track?.roles).flatMap(role => role?.resources);
    const totalResources = activeResourcesList?.length;

    const totalBillableResources = data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.billableCount), 0);
    const totalNonBillableResources = data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.nonBillableCount), 0);
    const totalShadowResources = data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.shadowCount), 0);
    const totalSupportResources = data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.supportCount), 0);

    let utilized = 100;
    const totalActiveResources = tracks?.totalDeliverableEmployeesByDate;
    const totalCount = totalBillableResources + totalNonBillableResources + totalShadowResources + totalSupportResources;

    if(totalActiveResources === totalCount){
      utilized = 100;
    } else {
      utilized = (totalCount/totalActiveResources)*100;
    }
    setUtilized(parseFloat(utilized.toFixed(2)));
    
    return {
      totalBillableResources: (totalBillableResources).toFixed(2),
      totalBillablePercentage: (data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.billablePercentage ? obj?.billablePercentage : 0), 0) / totalResources).toFixed(2) + '%',
      totalNonBillableResources: (totalNonBillableResources).toFixed(2),
      totalNonBillablePercentage: (data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.nonBillablePercentage ? obj?.nonBillablePercentage : 0), 0) / totalResources).toFixed(2) + '%',
      totalShadowResources: (totalShadowResources).toFixed(2),
      totalShadowPercentage: (data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.shadowPercentage ? obj?.shadowPercentage : 0), 0) / totalResources).toFixed(2) + '%',
      totalSupportResources: (totalSupportResources).toFixed(2),
      totalSupportPercentage: (data?.flatMap(track => track?.roles).flatMap(role => role?.resources).reduce((total, obj) => total + parseFloat(obj?.supportPercentage ? obj?.supportPercentage : 0), 0) / totalResources).toFixed(2) + '%',
    };    
  }


  return (
    <div className='resource-pool-dashboard'>
      <Box sx={{ width: '100%', boxShadow: 'inset 0 -1px 0 #eee' }} p={2}>
        <Grid
          container
          spacing={1}
          style={{
            justifyContent: 'between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveProjects}
              description="Active Projects"
              path={props?.permissions.some((perm) =>perm.name === 'View Projects' && perm.value === true) ? "project" : null}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveDeliverableEmployees}
              description="Active Resources"
              path={props?.permissions.some((perm) =>perm.name === 'View Resources' && perm.value === true) ? "resource" : null}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveDEV}
              description="DEV"
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveQA}
              description="QA"
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveBA}
              description="BA"
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActivePM}
              description="PM"
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color='#E6E9FF'
              count={tracks?.totalActiveSharedService}
              description="Shared Service"
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ width: '100%' }} p={2} pb={0}>
        {windowWidth <= 1350 && 
          <Typography mr={1} style={{ fontWeight: '500', fontSize: '12px' }}>Today Date : {moment(new Date()).format('YYYY-MM-DD')}</Typography>
        }
        <Box pb={2}>
          <div style={{ display:'flex', alignItems:'center' }}>
            {windowWidth > 1350 && 
            <Typography mr={3} style={{ fontWeight: '500', fontSize: '12px' }}>Today Date : {moment(new Date()).format('YYYY-MM-DD')}</Typography>
            }
            {/* Utilization Report */}
            {((appUser.Roles.split(',').includes('Super User') || props?.permissions.some((perm) =>perm.name === 'Download Utilization Report' && perm.value === true ))
             && !(type.length === 1 && type.includes('SHARED_SERVICE')) && type.length > 0) &&
              <Button
                sx={{
                  mt: { xs: 2, md: 0 },
                  fontSize: '11px',
                  fontWeight: 'normal'
                }}
                variant="contained"
                startIcon={<GetAppIcon fontSize="small" />}
                style={{ height: '1.75rem' }}
                onClick={handleDownload}
              >
                Download Utilization Report
              </Button>
            }
          </div>
          <div style={{ display:'flex', alignItems:'center', paddingTop: 15 }}>
            <div style={{ display:'flex', alignItems:'center', paddingRight: 15}}>
              <Typography mr={1} style={{ fontWeight: '500', fontSize: '12px' }}>Allocation Type</Typography>
              <MultiSelect
                formControlClass='type-multi-select-formcontrol'
                dropdownList={[
                  {
                    value: 'Billable',
                    name: 'Billable'
                  },
                  {
                    value: 'Non-Billable',
                    name: 'Non-Billable'
                  },
                  {
                    value: 'Shadow',
                    name: 'Shadow'
                  },
                  {
                    value: 'Support',
                    name: 'Support'
                  },
                  {
                    value: 'T&M',
                    name: 'T&M'
                  }
                ]}
                value={allocationType || []}
                onChange={(e: any) => {
                  setAllocationType(e.target.value);
                }}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', paddingLeft: 15}}>
              <Typography mr={1} style={{ fontWeight: '500', fontSize: '12px' }}>Category</Typography>
              <MultiSelect
                formControlClass='type-multi-select-formcontrol'
                dropdownList={[
                  {
                    value: 'SHARED_SERVICE',
                    name: 'Shared Service'
                  },
                  {
                    value: 'DELIVERY',
                    name: 'Delivery'
                  }
                ]}
                value={type || []}
                onChange={(e: any) => {
                  setType(e.target.value);
                }}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', paddingLeft: 30}}>
              <Typography mr={1} style={{ fontWeight: '500', fontSize: '12px' }}>Date</Typography>
              <DateRangePicker
                ref={myRef}
                initialSettings={getInitSettings()}
                onApply={(event, picker) => {
                  setStartDate(moment(picker.startDate).format('YYYY/MM/DD'));
                  setEndDate(moment(picker.endDate).format('YYYY/MM/DD'));
                  myRef.current?.setStartDate(picker.startDate);
                  myRef.current?.setEndDate(picker.endDate);
                  setDateChanged(true);
                }}
                onCancel={() => {
                  const today = new Date();
                  myRef.current?.setStartDate(today);
                  myRef.current?.setEndDate(today);
                  setStartDate(moment(today).format('YYYY/MM/DD'));
                  setEndDate(moment(today).format('YYYY/MM/DD'));
                  setDateChanged(true);
                }}
              >
                <div style={{ position: 'relative' }}>
                  <TextField
                    placeholder="YYYY/MM/DD - YYYY/MM/DD"
                    value={(startDate && startDate) + ' - ' + (endDate && endDate)}
                    id="date"
                    fullWidth
                    size="small"
                    style={{ paddingTop: 4, paddingLeft: 15, fontSize: '13px', width: '280px' }}
                  />
                  <CalendarTodayIcon
                    className="calendar-icon"
                    fontSize={'small'}
                  />
                </div>
              </DateRangePicker>
            </div>
          </div>
        </Box>
        <Grid
          container
          spacing={1}
          style={{
            justifyContent: 'between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              count={tracks?.totalDeliverableEmployeesByDate}
              description="Head Count"
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              count={trackDetails?.totalBillableResources}
              percentage="Billable"
              description={`(${trackDetails?.totalBillablePercentage})`}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              count={trackDetails?.totalNonBillableResources}
              percentage="Non-Billable"
              description={`(${trackDetails?.totalNonBillablePercentage})`}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              count={trackDetails?.totalShadowResources}
              percentage="Shadow"
              description={`(${trackDetails?.totalShadowPercentage})`}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              count={trackDetails?.totalSupportResources}
              percentage="Support"
              description={`(${trackDetails?.totalSupportPercentage})`}
            />
          </Grid>
          <Grid item xs={3} width='20%'>
            <DashboardCard
              color="rgba(232, 234, 238, 0.8)"
              title="Resource Utilization"
              count={utilized ? utilized + "%" : '0'}
              startDate={startDate}
              endDate={endDate}
              type={type}
              allocationType={allocationType}
              description={utilized === 100 ? "" : utilized < 100 ? "Underutilized by" : "Overutilized by"}
              increasedBy={utilized === 100 ? "" : utilized < 100 ? parseFloat((100 - utilized).toFixed(2)) + "%" : parseFloat((utilized - 100).toFixed(2)) + "%"}
              tooltip={true}
              tooltipDescription="Click to see Allocation Variances"
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ width: '100%', boxShadow: 'inset 0 -1px 0 #eee' }} p={2} pb={3} pt={1}>
        { tracks?.data?.length > 0 && <Tracks data={setOrder(tracks?.data)} trackDetails={trackDetails}/> }
      </Box>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
};

export default ResourcePoolDashboard;
